import { Link, useLocation } from 'react-router-dom';
import {
  HomeIcon,
  QuestionMarkCircleIcon,
  AcademicCapIcon,
  UsersIcon,
  ClipboardDocumentListIcon,
  ShieldExclamationIcon,
  ArrowPathRoundedSquareIcon,
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon,
  ChartBarIcon,
  ExclamationTriangleIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline';
import { useRef, useState, useEffect, useCallback } from 'react';
import clsx from 'clsx';
import { useRiskLevelStore } from '../../store/riskLevelStore';
import { ChevronRightIcon, ChevronLeftIcon } from '@heroicons/react/24/outline';
import { createContext, useContext } from 'react';

const navigation = [
  { name: 'Dashboard', href: '/', icon: HomeIcon },
  { 
    name: 'Response Plan', 
    href: '/response-plan', 
    icon: ShieldExclamationIcon,
    isCritical: true 
  },
  { 
    name: 'Crisis Response', 
    href: '/crisis-response', 
    icon: ExclamationTriangleIcon,
    isCritical: true 
  },
  { 
    name: 'War Room', 
    href: '/war-room', 
    icon: BuildingOfficeIcon,
    isCritical: true 
  },
  { 
    name: 'Communications', 
    href: '/communications', 
    icon: ChatBubbleLeftRightIcon,
    isCritical: true 
  },
  { name: 'Workflows', href: '/workflows', icon: ArrowPathRoundedSquareIcon },
  { name: 'Organization', href: '/organization', icon: UsersIcon },
  { 
    name: 'Readiness', 
    href: '/readiness', 
    icon: ChartBarIcon,
    children: [
      { name: 'Dashboard', href: '/readiness' },
      { name: 'Assessment', href: '/readiness/assessment' }
    ]
  },
  { name: 'How-to-Use', href: '/how-to-use', icon: QuestionMarkCircleIcon },
  { name: 'Training', href: '/training', icon: AcademicCapIcon },
  { name: 'Project Plan', href: '/project-plan', icon: ClipboardDocumentListIcon },
];

const getRiskLevelColor = (level: number) => {
  switch (level) {
    case 1:
      return 'bg-red-600';
    case 2:
      return 'bg-orange-500';
    case 3:
      return 'bg-yellow-500';
    case 4:
      return 'bg-blue-500';
    default:
      return '';
  }
};

interface SidebarContextType {
  isExpanded: boolean;
  setIsExpanded: (value: boolean) => void;
}

export const SidebarContext = createContext<SidebarContextType>({
  isExpanded: true,
  setIsExpanded: () => {}
});

export const useSidebarContext = () => useContext(SidebarContext);

export default function Sidebar() {
  const location = useLocation();
  const sidebarContext = useSidebarContext();
  const { isExpanded } = sidebarContext;
  const riskLevel = useRiskLevelStore((state) => state.level);
  const navRef = useRef<HTMLDivElement>(null);
  const [showScrollButtons, setShowScrollButtons] = useState(false);
  const [isAtTop, setIsAtTop] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(false);

  const toggleSidebar = useCallback(() => {
    sidebarContext.setIsExpanded(!isExpanded);
  }, [isExpanded, sidebarContext]);

  const criticalNavItems = navigation.filter(item => item.isCritical);
  const nonCriticalNavItems = navigation.filter(item => !item.isCritical && item.name !== 'Dashboard');

  useEffect(() => {
    const nav = navRef.current;
    if (nav) {
      setShowScrollButtons(nav.scrollHeight > nav.clientHeight);
      checkScrollPosition();
    }
  }, []);

  const checkScrollPosition = () => {
    const nav = navRef.current;
    if (nav) {
      setIsAtTop(nav.scrollTop === 0);
      setIsAtBottom(nav.scrollTop + nav.clientHeight >= nav.scrollHeight);
    }
  };

  const scrollNav = (direction: 'up' | 'down') => {
    const nav = navRef.current;
    if (nav) {
      const scrollAmount = 100;
      nav.scrollBy({
        top: direction === 'up' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
    }
  };

  return (
    <aside className={clsx(
        "fixed inset-y-0 left-0 bg-gray-900 flex flex-col transition-all duration-200 ease-in-out z-40 transform",
        "lg:translate-x-0",
        !isExpanded && "-translate-x-full lg:translate-x-0",
        isExpanded ? "w-64" : "w-20"
      )}>
        <div className="flex-shrink-0 h-16 flex items-center justify-between px-4">
          <h1 className={clsx(
            "text-white font-bold transition-opacity duration-200",
            isExpanded ? "text-xl opacity-100" : "text-xs opacity-0"
          )}>
            RESILENT™
          </h1>
          <button
            onClick={toggleSidebar}
            className="p-1 rounded-full bg-gray-800 text-gray-300 hover:bg-gray-700"
          >
            {isExpanded ? (
              <ChevronLeftIcon className="h-5 w-5" />
            ) : (
              <ChevronRightIcon className="h-5 w-5" />
            )}
          </button>
        </div>

        <div className="relative flex-1 flex flex-col overflow-hidden">
        {showScrollButtons && !isAtTop && (
          <button
            onClick={() => scrollNav('up')}
            className="absolute right-2 top-2 z-10 p-1 rounded-full bg-gray-800 text-gray-300 hover:bg-gray-700"
          >
            <ChevronUpIcon className="h-5 w-5" />
          </button>
        )}

        <nav 
          ref={navRef}
          className="flex-1 mt-6 px-2 space-y-1 overflow-y-auto scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent"
          onScroll={checkScrollPosition}
          style={{ maxHeight: 'calc(100vh - 4rem)' }}
        >
          {/* Dashboard at the top */}
          <div className="space-y-1">
            <Link
              to="/"
              className={clsx(
                location.pathname === '/'
                  ? 'bg-gray-800 text-white'
                  : 'text-gray-300 hover:bg-gray-700',
                'group flex items-center px-2 py-2 text-base font-medium rounded-md'
              )}
            >
              <HomeIcon className="mr-4 h-6 w-6 flex-shrink-0" aria-hidden="true" />
              Dashboard
            </Link>
          </div>

          {/* Critical section with background */}
          <div className="relative my-2">
            <div className={clsx(
              'absolute inset-0 opacity-20 rounded-lg',
              getRiskLevelColor(riskLevel)
            )} />
            <div className="relative space-y-1 py-2">
              {criticalNavItems.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={clsx(
                    location.pathname === item.href
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-700',
                    'group flex items-center px-2 py-2 text-base font-medium rounded-md',
                    !isExpanded && 'justify-center'
                  )}
                >
                  <item.icon className={clsx(
                    "h-6 w-6 flex-shrink-0",
                    isExpanded && "mr-4"
                  )} aria-hidden="true" />
                  {isExpanded && item.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Regular items after critical section */}
          <div className="space-y-1">
            {nonCriticalNavItems.map((item) => (
              <div key={item.name}>
                <Link
                  to={item.href}
                  className={clsx(
                    location.pathname === item.href
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-700',
                    'group flex items-center px-2 py-2 text-base font-medium rounded-md'
                  )}
                >
                  <item.icon className="mr-4 h-6 w-6 flex-shrink-0" aria-hidden="true" />
                  {item.name}
                </Link>
                {item.children && (
                  <div className="ml-8 space-y-1">
                    {item.children.map((child) => (
                      <Link
                        key={child.name}
                        to={child.href}
                        className={clsx(
                          location.pathname === child.href
                            ? 'bg-gray-800 text-white'
                            : 'text-gray-300 hover:bg-gray-700',
                          'group flex items-center px-2 py-2 text-sm font-medium rounded-md'
                        )}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </nav>

        {showScrollButtons && !isAtBottom && (
          <button
            onClick={() => scrollNav('down')}
            className="absolute right-2 bottom-2 z-10 p-1 rounded-full bg-gray-800 text-gray-300 hover:bg-gray-700"
          >
            <ChevronDownIcon className="h-5 w-5" />
          </button>
        )}
        </div>
      </aside>
  );
}